import React, { useState, useEffect } from 'react';
import { Box, TextField, Tooltip, IconButton, TableCell, Autocomplete } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { EditableComponentProps } from '../../CompanycodeTranslation/components/CreationModal/types';
import { HSNRowData } from '../types/HSNRowType';
import {
  useCreateHSNSACMasterMutation,
} from '../Api/searchHSN.api';
import { showSuccessSnackbar, showWarningSnackbar } from '../../../components/snackBar/snakbar';


const codeTypes = [
  { label: 'HSN Code', value: 'HSN' },
  { label: 'SAC Code', value: 'SAC' },
];

// Extended props to include codeType
interface HSNEditableComponentProps extends EditableComponentProps<HSNRowData> {
  codeType?: 'HSN' | 'SAC' | '';
}

// HSN/SAC Editable Component with strict typing and API integration
export const HSNEditableComponent: React.FC<HSNEditableComponentProps> = ({
  row,
  data,
  onCancel,
  isNewRow = false,
  onSave,
  index,
  codeType = '',
}) => {
  // Determine initial codeType based on prop
  const getInitialCodeType = (): string => {
    if (codeType === 'HSN') return 'HSN';
    if (codeType === 'SAC') return 'SAC';
    // If codeType is empty, use row's codeType or default to 'HSN'
    return row?.codeType || 'HSN';
  };

  const [formData, setFormData] = useState<HSNRowData>(() => {
    // If it's a new row, initialize with default values
    if (isNewRow) {
      return {
        id: Date.now(), // Temporary ID
        code: '',
        codeType: getInitialCodeType(),
        description: '',
        isEditable: true,
        isDeletable: true,
        isActive: true,
      } as HSNRowData;
    }
    return { ...row };
  });
  const [isSaving, setIsSaving] = useState(false);
  const [createHSNSACMaster] = useCreateHSNSACMasterMutation();

  useEffect(() => {
    if (!isNewRow) {
      setFormData({ ...row });
    }
  }, [row, isNewRow]);

  const handleCodeChange = (value: string) => {
    // Only allow numeric values
    const regex = /^[0-9]*$/;
    if (regex.test(value) || value === '') {
      setFormData((prev) => ({ ...prev, code: value }));
    }
  };

  const handleCodeTypeChange = (newValue: { label: string; value: string } | null) => {
    if (newValue) {
      setFormData((prev) => ({ ...prev, codeType: newValue.value }));
    }
  };

  const handleDescriptionChange = (value: string) => {
    // Limit description to 250 characters
    if (value.length <= 250) {
      setFormData((prev) => ({ ...prev, description: value }));
    }
  };

  const handleSave = async () => {
    // Validation
    if (!formData.code || formData.code.trim() === '' || formData.code === '0') {
      showWarningSnackbar(
        formData.codeType === 'HSN' ? 'HSN Code is required' : 'SAC Code is required'
      );
      return;
    }

    if (!formData.codeType || formData.codeType === '') {
      showWarningSnackbar('Code Type is required');
      return;
    }

    if (!formData.description || formData.description.trim() === '') {
      showWarningSnackbar('Description is required');
      return;
    }

    if (formData.description.length > 250) {
      showWarningSnackbar('Description must be less than 250 characters');
      return;
    }

    setIsSaving(true);
    try {
      // Handle newlines in description - join with ** like in AddHSNModal
      const comments = formData.description.split('\n');
      const descriptionFormatted = comments.join('**');

      if (isNewRow) {
        const result = await createHSNSACMaster({
          createHSNSACMasterInput: {
            codetype: formData.codeType,
            hsnsaccode: String(formData.code),
            description: descriptionFormatted,
          },
        }).unwrap();

        if (result?.createHSNSACMaster?.statuscode === 'Success') {
          showSuccessSnackbar(
            formData.codeType === 'HSN'
              ? 'HSN Code created successfully'
              : 'SAC Code created successfully'
          );
          if (onSave) {
            const apiResult = result.createHSNSACMaster;
            const newRow: HSNRowData = {
              id: apiResult?.hsnsacid ?? Date.now(),
              code: apiResult?.hsnsaccode ?? formData.code,
              codeType: apiResult?.codetype ?? formData.codeType,
              description: apiResult?.description?.replace(/\*\*/g, '\n') ?? formData.description,
              raw: apiResult as any,
              isEditable: true,
              isDeletable: true,
              isActive: true,
            };
            onSave(newRow);
          }
        } else {
          showWarningSnackbar(
            result?.createHSNSACMaster?.statusmessage || 'Failed to create HSN/SAC Code'
          );
        }
      } else {
        const rawData = formData.raw;
        if (!rawData?.hsnsacid) {
          showWarningSnackbar('Invalid HSN/SAC ID for update');
          setIsSaving(false);
          return;
        }

        const result = await createHSNSACMaster({
          createHSNSACMasterInput: {
            codetype: formData.codeType,
            hsnsaccode: String(formData.code),
            description: descriptionFormatted,
          },
        }).unwrap();

        if (result?.createHSNSACMaster?.statuscode === null) {
          showSuccessSnackbar(
            formData.codeType === 'HSN'
              ? 'HSN Code updated successfully'
              : 'SAC Code updated successfully'
          );
          if (onSave) {
            const apiResult = result.createHSNSACMaster;
            const updatedRow: HSNRowData = {
              ...formData,
              code: apiResult?.hsnsaccode ?? formData.code,
              codeType: apiResult?.codetype ?? formData.codeType,
              description: apiResult?.description?.replace(/\*\*/g, '\n') ?? formData.description,
              raw: apiResult as any,
            };
            onSave(updatedRow);
          }
        } else {
          showWarningSnackbar(
            result?.createHSNSACMaster?.statusmessage || 'Failed to update HSN/SAC Code'
          );
        }
      }
    } catch (error: any) {
      console.error(`Error ${isNewRow ? 'creating' : 'updating'} HSN/SAC row:`, error);
      let errorMessage = error?.data?.message || error?.message;
      
      // Handle RDSHttp error format like in AddHSNModal
      if (error?.errors?.[0]?.message) {
        try {
          const fullMessage = JSON.parse(error.errors[0].message.replace('RDSHttp:', '')).message;
          errorMessage = fullMessage.replace('ERROR: ', '').split(';')[0];
        } catch (e) {
          errorMessage = error.errors[0].message;
        }
      }
      
      showWarningSnackbar(errorMessage || `Failed to ${isNewRow ? 'create' : 'update'} HSN/SAC Code`);
    } finally {
      setIsSaving(false);
    }
  };

  const currentCodeType = codeTypes.find((ct) => ct.value === formData.codeType) || codeTypes[0];
  
  // Determine if codeType field should be disabled
  // Disable when codeType prop is "HSN" or "SAC", enable when it's ""
  const isCodeTypeDisabled = codeType === 'HSN' || codeType === 'SAC';

  // Update formData when codeType prop changes (for new rows)
  useEffect(() => {
    if (isNewRow && codeType) {
      setFormData((prev) => ({
        ...prev,
        codeType: codeType,
      }));
    }
    const element=document.getElementById(codeType===""?"codeType":"code");
     element?.focus();
  }, [codeType, isNewRow]);

  return (
    <>
      <TableCell align="center">
        {isNewRow ? '' : index !== undefined ? index + 1 : formData.id || '-'}
      </TableCell>
      <TableCell>
        <Autocomplete
          disablePortal
          id="codeType"
          disableClearable
          disabled={isCodeTypeDisabled} // Disable when codeType is "HSN" or "SAC"
          options={codeTypes}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={currentCodeType}
          onChange={(e, newValue) => {
            if (newValue) {
              handleCodeTypeChange(newValue);
            }
          }}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              required
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  outline: 'none !important',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none !important',
                },
              }}
            />
          )}
        />
      </TableCell>
      <TableCell>
        <TextField
          value={formData.code}
          id="code"
          onChange={(e) => handleCodeChange(e.target.value)}
          fullWidth
          size="small"
          required
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              outline: 'none !important',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none !important',
            },
          }}
        />
      </TableCell>
      <TableCell>
        <TextField
          value={formData.description}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          fullWidth
          size="small"
          required
          multiline
          rows={2}
          inputProps={{ maxLength: 250 }}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              outline: 'none !important',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none !important',
            },
          }}
        />
      </TableCell>

      <TableCell align="center">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Tooltip title="Save" placement="top" disableInteractive arrow>
            <IconButton
              onClick={handleSave}
              disabled={isSaving}
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                backgroundColor: '#83ff7b7a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: '0.2s ease',
                '&:hover': {
                  backgroundColor: '#bff0bf',
                  transform: 'scale(1.08)',
                },
                '&:disabled': {
                  backgroundColor: '#cdecdc',
                  cursor: 'not-allowed',
                },
              }}
            >
              <CheckIcon sx={{ fontSize: 15, color: '#34994f' }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Cancel" placement="top" disableInteractive arrow>
            <IconButton
              onClick={onCancel}
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                backgroundColor: '#fbc7c78c',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: '0.2s ease',
                '&:hover': {
                  backgroundColor: '#f7c8c8',
                  transform: 'scale(1.08)',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 15, color: '#eb6a6a' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </>
  );
};

